import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibCheckbox } from '@chewy/kib-choices-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSelect } from '@chewy/kib-fields-new-react'
import {
  KibCloseIcon,
  KibIcon,
  KibWarningFillIcon,
} from '@chewy/kib-icons-react'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import classNames from 'classnames'

import ConversationMessageFormattingArea from '~/components/dashboard/communications/ConversationMessageFormattingArea'
import SystemMessageAlert from '~/components/dashboard/communications/conversations-chat/SystemMessageAlert'
import { MessageType } from '~/constants/communications'
import FeatureToggle from '~/constants/featureToggle'
import {
  type TransportOptions,
  useMessageFormatting,
} from '~/store/hooks/conversations'
import { getFeatureToggle } from '~/store/reducers/constants'

import type { MessageFormattingAreaProps } from './MessageFormattingArea'

import '@chewy/kib-patterns/dist/styles/kib-icon.css'
import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-select.css'
import styles from './KibMessageFormattingArea.module.scss'

const KibMessageFormattingArea = ({
  conversationId,
  disabled = false,
  hideStatusUpdateButtons = false,
}: MessageFormattingAreaProps) => {
  const { t } = useTranslation(['Common', 'Communications', 'Clients'])
  const isUnifiedConversationThreading = useSelector(
    getFeatureToggle(
      FeatureToggle.UNIFIED_CONVERSATION_TRANSPORTATION_THREADING,
    ),
  )
  const {
    messageFormattingAreaExpanded,
    handleCloseMessageFormattingArea,
    canSendMessage,
    clientId,
    conversation,
    messageField,
    patientId,
    appointment,
    conversationFormattingAreaRef,
    transport,
    showSendButton,
    isLoading,
    handleMessageSend,
    showReplyButton,
    handleReplyClick,
    showReplyInNewThreadButton,
    handleReplyInNewThreadClick,
    handleMessageDraftSend,
    shouldShowCheckbox,
    isMedical,
    onInternalNoteAdd,
    isDeliveryFailed,
    onArchiveAction,
    archiveActionTitle,
    isSystemMessageComposerSuppressed,
    messageError,
    transportField,
    transportOptions,
    getSendActionName,
  } = useMessageFormatting({ conversationId })

  const nullifiedTransportValue =
    transportField.value === MessageType.INTERNAL_NOTE
      ? null
      : transportField.value

  if (isSystemMessageComposerSuppressed) {
    return (
      <KibContainer
        className={classNames(
          styles.messageInputContainer,
          styles.systemMessage,
        )}
      >
        <SystemMessageAlert {...{ clientId, patientId }} />
      </KibContainer>
    )
  }

  return (
    <KibContainer className={styles.messageInputContainer}>
      {messageFormattingAreaExpanded && (
        <KibGrid className={styles.header}>
          {isUnifiedConversationThreading && (
            <KibSelect
              required
              className={styles.transportSelect}
              invalid={!transportField.valid}
              label={t('Communications:MESSAGE_FORMATTING_AREA.MESSAGE_TYPE')}
              value={transportField.value}
              onChange={(event) => transportField.setValue(event.target.value)}
            >
              <option hidden value="">
                {`${t('Common:CHOOSE_ONE')}...`}
              </option>
              {transportOptions?.map((option: TransportOptions) => (
                <option
                  disabled={option.disabled}
                  hidden={option.hidden}
                  key={option.value || 'internal-note'}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </KibSelect>
          )}
          <KibButtonNew
            iconOnly
            emphasis="tertiary"
            icon={<KibCloseIcon />}
            size="small"
            theme="utility"
            onClick={handleCloseMessageFormattingArea}
          >
            {t('Common:CLOSE_ACTION')}
          </KibButtonNew>
        </KibGrid>
      )}
      {(isUnifiedConversationThreading || canSendMessage) &&
        messageFormattingAreaExpanded && (
          <ConversationMessageFormattingArea
            hideEmail
            hideTo
            isChirp
            showAttachment
            clientId={clientId}
            eventId={conversation?.eventId}
            hideTitle={isUnifiedConversationThreading}
            isShowSubject={false}
            maxEditorHeight={200}
            messageField={messageField}
            minEditorHeight={150}
            patientId={patientId || appointment?.patient}
            ref={conversationFormattingAreaRef}
            transport={
              isUnifiedConversationThreading
                ? nullifiedTransportValue
                : transport
            }
          />
        )}
      {messageFormattingAreaExpanded && messageError && (
        <span className={styles.messageError}>{messageError}</span>
      )}
      <KibGrid
        className={classNames({
          [styles.messageCompose]: messageFormattingAreaExpanded,
        })}
        valign="center"
      >
        {!messageFormattingAreaExpanded ? (
          <>
            {(isUnifiedConversationThreading || showReplyButton) && (
              <>
                <KibGridItem>
                  <KibButtonNew
                    disabled={isLoading || disabled}
                    onClick={handleReplyClick}
                  >
                    {t('Common:REPLY')}
                  </KibButtonNew>
                </KibGridItem>
                {showReplyInNewThreadButton && (
                  <KibGridItem>
                    <KibButtonNew
                      disabled={isLoading || disabled}
                      emphasis="secondary"
                      onClick={handleReplyInNewThreadClick}
                    >
                      {t('Communications:REPLY_IN_NEW_THREAD')}
                    </KibButtonNew>
                  </KibGridItem>
                )}
              </>
            )}
            <KibGrid
              align="right"
              className={styles.nonReplyButtons}
              valign="center"
            >
              {!isUnifiedConversationThreading && (
                <KibGridItem>
                  <KibButtonNew
                    disabled={isLoading}
                    emphasis="secondary"
                    onClick={onInternalNoteAdd}
                  >
                    {t('Common:ADD_INTERNAL_NOTE')}
                  </KibButtonNew>
                </KibGridItem>
              )}
              {!hideStatusUpdateButtons && !isDeliveryFailed && (
                <KibGridItem>
                  <KibButtonNew
                    disabled={isLoading}
                    emphasis="secondary"
                    onClick={onArchiveAction}
                  >
                    {archiveActionTitle}
                  </KibButtonNew>
                </KibGridItem>
              )}
            </KibGrid>
          </>
        ) : (
          <>
            {(isUnifiedConversationThreading || canSendMessage) &&
              showSendButton && (
                <KibGridItem>
                  <KibButtonNew
                    disabled={isLoading}
                    onClick={handleMessageSend}
                  >
                    {getSendActionName()}
                  </KibButtonNew>
                </KibGridItem>
              )}
            {!canSendMessage && !isUnifiedConversationThreading && (
              <KibGrid className={styles.smsDisabled} valign="center">
                <KibGridItem>
                  <KibIcon circle size="xs">
                    <KibWarningFillIcon />
                  </KibIcon>
                </KibGridItem>
                <KibGridItem>
                  <span>{t('Clients:SMS_CONSENT_DISABLED')}</span>
                </KibGridItem>
              </KibGrid>
            )}
            <KibGridItem>
              <KibButtonNew
                disabled={isLoading}
                emphasis="secondary"
                onClick={handleMessageDraftSend}
              >
                {t('Common:SAVE_AS_DRAFT')}
              </KibButtonNew>
            </KibGridItem>
            {shouldShowCheckbox && (
              <KibGridItem>
                <KibCheckbox
                  className={styles.checkbox}
                  label={isMedical.label}
                  selectedValue={isMedical.value}
                  onChange={() => isMedical.setValue(!isMedical.value)}
                />
              </KibGridItem>
            )}
          </>
        )}
      </KibGrid>
    </KibContainer>
  )
}

export default KibMessageFormattingArea
